import React from "react";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import GlobalSearchItemHeader from "./GlobalSearchItemHeader";
import AppIcon from "ui/components/icons/AppIcon";
import { iconPeopleOutline } from "ui/components/icons";
import { MeetingContent } from "data/@types/GlobalSearchData";
import useHighlight, { globalSearchMarkStyle } from "data/hooks/useHighlight";
import { useGlobalSearchContext } from "../SearchProvider";
import DotDivider from "ui/components/data-display/DotDivider";
import ExpandableArea from "ui/components/surfaces/ExpandableArea";
import {
  getCaParticipants,
  getMgrParticipants,
} from "utils/meetingParticipant";
import { yesNo } from "utils/yesNoFilter";
import { Link } from "react-router-dom";
import { formatDateTimeInUserTimeZone } from "utils/date";
import { valueOrPlaceholder } from "utils/form";

const LabelledTextGrid = ({
  label,
  text,
  shouldHighlight = true,
  dataTestId,
  tooltipText,
}: {
  label: string;
  text: string | null;
  shouldHighlight?: boolean;
  dataTestId: string;
  tooltipText?: string;
}) => {
  const { search } = useGlobalSearchContext();
  const { highlight } = useHighlight(search, globalSearchMarkStyle);

  return (
    <Grid size={{ sm: 12, md: 6, lg: 4 }} sx={{ height: "1.1em" }}>
      <Tooltip title={tooltipText || text}>
        <Typography
          variant="body2"
          component="span"
          data-test-id={dataTestId}
          sx={{
            fontSize: "11px",
            color: "text.secondary",
            display: "block",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {label}
          {": "}
          {shouldHighlight && text ? (
            <span dangerouslySetInnerHTML={{ __html: highlight(text) }} />
          ) : (
            <>{valueOrPlaceholder(text)}</>
          )}
        </Typography>
      </Tooltip>
    </Grid>
  );
};

const MeetingItem = ({ meeting }: { meeting: MeetingContent }) => {
  const { search, handleClose } = useGlobalSearchContext();
  const { highlight } = useHighlight(search, globalSearchMarkStyle);

  const isProductOrStrategy = ["Product", "Strategy"].includes(
    meeting.entityType,
  );

  const caParticipants = getCaParticipants(meeting.participants);

  const mgrParticipants = getMgrParticipants(meeting.participants);

  const location = (() => {
    switch (meeting.location?.type) {
      case "C|A Location":
        return meeting.location.caOffice;
      case "Manager Office Location":
        return meeting.location.managerLocation;
      case "Other Location":
        return (
          (meeting.location.other &&
            meeting.location.otherCountry &&
            `${meeting.location.other}, ${meeting.location.otherCountry}`) ||
          meeting.location.other ||
          meeting.location.otherCountry
        );
      default:
        return null;
    }
  })();

  const detailsRoute = `/meeting/${meeting.id}`;

  return (
    <Box
      sx={{
        padding: "6px 0",
        display: "inline-grid",
        gridTemplateColumns: "25px auto",
        width: "100%",
        alignItems: "center",
      }}
    >
      <AppIcon
        icon={iconPeopleOutline}
        sx={{ fontSize: "1em", gridRow: "span 5", alignSelf: "start" }}
        data-test-id="meeting-icon"
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: "normal",
          fontSize: "12px",
          color: "text.primary",
        }}
      >
        {formatDateTimeInUserTimeZone(meeting.startDateTime)}
      </Typography>
      <GlobalSearchItemHeader
        entityType={meeting.entityType}
        firm={meeting.firm!}
        entity={meeting.entity}
      />
      <Link
        to={detailsRoute}
        style={{ textDecoration: "none" }}
        onClick={handleClose}
      >
        <Typography
          sx={{
            fontSize: "11px",
            color: "text.secondary",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <span
            style={{ fontWeight: 600 }}
            dangerouslySetInnerHTML={{
              __html: highlight(meeting.authorName) || "--",
            }}
          />

          {isProductOrStrategy && (
            <>
              <DotDivider style={{ mx: 1 }} component="span" />
              <span>Rating:&nbsp;</span>
              {meeting.lastRating ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: highlight(meeting.lastRating) || "--",
                  }}
                />
              ) : (
                <span>--</span>
              )}
            </>
          )}
          <DotDivider style={{ mx: 1 }} component="span" />
          <span
            dangerouslySetInnerHTML={{
              __html: highlight(meeting.type!) || "--",
            }}
          />
        </Typography>

        <ExpandableArea
          dividerSx={{
            background: (theme) => theme.palette.grey[700],
            marginY: 0.5,
          }}
        >
          <Grid container sx={{ paddingBottom: 1 }}>
            {/* Row 1 */}

            <LabelledTextGrid
              label="Scheduled by"
              text={meeting.authorName || "No Scheduler"}
              dataTestId="scheduled-by"
            />

            <LabelledTextGrid
              label="Start Date and Time"
              text={formatDateTimeInUserTimeZone(meeting.startDateTime)}
              tooltipText={formatDateTimeInUserTimeZone(meeting.startDateTime, {
                showTimeZoneCode: true,
              })}
              shouldHighlight={false}
              dataTestId="start-date-time"
            />

            <LabelledTextGrid
              label="Investment Strategy"
              text={meeting.investmentStrategy}
              dataTestId="investment-strategy"
            />

            {/* Row 2 */}

            <LabelledTextGrid
              label="Meeting Location"
              text={location}
              dataTestId="meeting-location"
            />

            <LabelledTextGrid
              label="Annual Meeting Materials Received"
              text={yesNo(meeting.annualMaterialsReceived)}
              shouldHighlight={false}
              dataTestId="materials-received"
            />

            <LabelledTextGrid
              label="Closed Private"
              text={yesNo(meeting.closed)}
              shouldHighlight={false}
              dataTestId="closed-private"
            />

            {/* Row 3 */}

            <LabelledTextGrid
              label="C|A Participants"
              text={caParticipants}
              dataTestId="ca-participants"
            />

            <LabelledTextGrid
              label="Manager Participants"
              text={mgrParticipants}
              dataTestId="mgr-participants"
            />

            <LabelledTextGrid
              label="Type"
              text={meeting.type!}
              dataTestId="meeting-type"
            />
          </Grid>
        </ExpandableArea>
      </Link>
    </Box>
  );
};

export default MeetingItem;
